import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import VariablesExportModal from './VariablesExportModal'
import paletteOutline from '../../../assets/images/palette-outline.svg'
import Loader from '../shared/Loader'
import {
  ExportHeader,
  CollectInfo,
  ErrorAlert,
  CustomExportForm
} from '../shared/Export'
import { formatDate } from '../../utils/dateHelpers'

const EMPTY_PREFERENCES = {}

const shouldIgnoreFilters = returnUrl =>
  returnUrl && returnUrl.includes('/drafts') && !returnUrl.includes('/resources')

const ResourcesExport = ({
  collectId, draftId = null, returnUrl = null, savedPreferences = EMPTY_PREFERENCES
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [ensemble, setEnsemble] = useState(null)
  const [selectedQuestions, setSelectedQuestions] = useState({})
  const [showVariablesModal, setShowVariablesModal] = useState(false)

  const getResourcesFromUrl = useCallback(() => ({
    resourceIds: new URLSearchParams(window.location.search).get('resource_ids')?.split(',') || [],
    allResources: new URLSearchParams(window.location.search).get('all_resources') === 'true',
    query: new URLSearchParams(window.location.search).get('q'),
    resourceStatus: new URLSearchParams(window.location.search).get('resource_status'),
    startDate: new URLSearchParams(window.location.search).get('start_date'),
    endDate: new URLSearchParams(window.location.search).get('end_date')
  }), [])


  const handleExport = useCallback(exportData => {
    const form = document.getElementById('export-form')
    const selectedResources = getResourcesFromUrl()

    const sectionIds = Object.entries(selectedQuestions)
      .filter(([, questions]) => questions.length > 0)
      .map(([sectionId]) => sectionId)
    const questionIds = Object.values(selectedQuestions).flat()

    const formInputs = [
      { name: 'section_ids', value: sectionIds.join(',') },
      { name: 'question_ids', value: questionIds.join(',') }
    ]

    if (exportData.variableIds)
      formInputs.push({ name: 'variable_ids', value: exportData.variableIds.join(',') })


    if (selectedResources.resourceIds?.length > 0)
      formInputs.push({ name: 'resource_ids', value: selectedResources.resourceIds.join(',') })


    if (selectedResources.allResources)
      formInputs.push({ name: 'all_resources', value: 'true' })


    if (selectedResources.query)
      formInputs.push({ name: 'q', value: selectedResources.query })


    if (selectedResources.resourceStatus)
      formInputs.push({ name: 'resource_status', value: selectedResources.resourceStatus })


    if (selectedResources.startDate)
      formInputs.push({ name: 'start_date', value: selectedResources.startDate })


    if (selectedResources.endDate)
      formInputs.push({ name: 'end_date', value: selectedResources.endDate })

    // When exporting from drafts index page (not resources page), ignore filters to export all resources
    if (shouldIgnoreFilters(returnUrl))
      formInputs.push({ name: 'ignore_filters', value: 'true' })


    formInputs.forEach(({ name, value }) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    })

    if (window.plausible)
      window.plausible('Collect Excel Exported')

    form.submit()
  }, [selectedQuestions, getResourcesFromUrl, returnUrl])

  const handleSubmit = useCallback(event => {
    event.preventDefault()

    if (data?.variables?.length === 0) {
      handleExport({
        variables: []
      })
    } else
      setShowVariablesModal(true)
  }, [data, handleExport])

  const getTotalSelectedQuestions = useCallback(() => Object.values(selectedQuestions).reduce(
    (total, questions) => total + questions.length, 0
  ), [selectedQuestions])

  const handleModalClose = useCallback(() => setShowVariablesModal(false), [])
  
  const selectAllQuestions = useCallback(sections => {
    const selection = {}
    sections.forEach(section => {
      selection[section.id] = section.questions.map(question => question.id)
    })
    return selection
  }, [])

  const applyPreferences = useCallback((collectData, preferences) => {
    const newSelection = {}
    
    collectData.sections.forEach(section => {
      const sectionQuestions = section.questions
        .filter(question => preferences.question_ids.includes(question.id))
        .map(question => question.id)
      
      if (sectionQuestions.length > 0) 
        newSelection[section.id] = sectionQuestions
    })
    
    setSelectedQuestions(newSelection)
  }, [])

  const getRedirectBackUrl = useCallback(() => {
    if (returnUrl) 
      return returnUrl
    if (draftId)
      return `/collects/${collectId}/drafts/${draftId}/resources`
    return '/collects'
  }, [collectId, draftId, returnUrl])

  const getExportUrl = useCallback(() => {
    const baseUrl = `/collects/${collectId}`
    return draftId ? `${baseUrl}/drafts/${draftId}/resources_exports` : `${baseUrl}/resources_exports`
  }, [collectId, draftId])

  useEffect(() => {
    const headers = {
      'accept': 'application/json',
      'Content-Type': 'application/json'
    }

    fetch(`/collects/${collectId}.json`, { method: 'GET', headers })
      .then(response => {
        if (!response.ok)
          throw new Error('Erreur lors du chargement des données')

        return response.json()
      })
      .then(responseData => {
        setData(responseData)
        const foundEnsemble = responseData.ensembles.find(item => item.id === responseData.ensemble_id)
        setEnsemble(foundEnsemble)

        if (savedPreferences && savedPreferences.excel) 
          applyPreferences(responseData, savedPreferences.excel)
        else 
          setSelectedQuestions(selectAllQuestions(responseData.sections))
        
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [collectId, savedPreferences, applyPreferences, selectAllQuestions])

  if (loading)
    return <Loader text="Chargement des données..." size="small" />

  if (error)
    return <ErrorAlert message={error} />

  if (!data)
    return null
  const currentDraft = data.drafts.find(draftItem => draftItem.id === draftId)
  const startingAt = currentDraft?.starting_at ? formatDate(currentDraft.starting_at) : null

  return (
    <>
      <div className="modal-content border">
        <ExportHeader
          title="Personnaliser l'export Excel"
          icon={paletteOutline}
          iconAlt="Customize"
          cancelUrl={getRedirectBackUrl()}
        />
        <div className="modal-body">
          <CollectInfo
            collectName={data.name}
            startingAt={startingAt}
            ensembleName={ensemble?.name}
          />
          <CustomExportForm
            exportUrl={getExportUrl()}
            sections={data.sections}
            selectedQuestions={selectedQuestions}
            title="Sélectionnez les sections et questions à exporter au niveau de la collecte"
            onQuestionChange={setSelectedQuestions}
            onSubmit={handleSubmit}
          />
        </div>
        <div className="modal-footer">
          <div className="d-flex justify-content-end align-items-center w-100">
            <button
              type="submit"
              form="export-form"
              className="btn btn-success"
              disabled={getTotalSelectedQuestions() === 0}
            >
              {data.variables?.length === 0 ? 'Exporter' : 'Suivant'}
            </button>
          </div>
        </div>
      </div>
      {showVariablesModal && data ? (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <VariablesExportModal
              variables={data.variables}
              collect={data}
              startingAt={startingAt}
              savedPreferences={savedPreferences.excel || {}}
              onClose={handleModalClose}
              onExport={handleExport}
            />
          </div>
        </div>
      ) : null}
    </>
  )
}

ResourcesExport.propTypes = {
  collectId: PropTypes.number.isRequired
}

export default ResourcesExport
